import {
  Canvas,
  Circle,
  Control,
  FabricImage,
  FabricObject,
  Path,
  Textbox,
  Triangle,
  Polyline,
  controlsUtils,
} from 'fabric';

import { grey1, grey4, purple6 } from 'approot/shared/styles/colours.style';
import { BASE_LOCKED_STYLE, TEXT_PLACEHOLDER } from '../draw-canvas.constants';
import isMobileBrowser from 'approot/shared/logic/is-mobile-browser';

import trash from './trash.png';

export function customiseControlStyles(
  obj: FabricObject,
  isLocked: boolean = false
) {
  if (isLocked) {
    obj.set({
      ...BASE_LOCKED_STYLE,
    });
  } else {
    obj.set({
      borderOpacityWhenMoving: 1,
      cornerColor: grey1,
      cornerStrokeColor: grey4,
      cornerStyle: 'circle',
      transparentCorners: false,
      borderColor: purple6,
    });
  }
}

export function customiseShapeControlPoints(
  obj: FabricObject,
  isLocked: boolean = false
) {
  if (!isLocked) {
    obj.setControlsVisibility({
      tr: true,
      tl: true,
      bl: true,
      br: true,
      mt: false,
      mb: false,
      mtr: true,
      ml: false,
      mr: false,
    });
  }
}

export function customiseSquareControlPoints(
  obj: FabricObject,
  isLocked: boolean = false
) {
  if (!isLocked) {
    obj.setControlsVisibility({
      tr: true,
      tl: true,
      bl: true,
      br: true,
      mt: false,
      mb: false,
      mtr: true,
      ml: false,
      mr: false,
    });
  }
}

export function customiseRectControlPoints(
  obj: FabricObject,
  isLocked: boolean = false
) {
  if (!isLocked) {
    obj.setControlsVisibility({
      tr: true,
      tl: true,
      bl: true,
      br: true,
      mt: true,
      mb: true,
      mtr: true,
      ml: true,
      mr: true,
    });
  }
}

export function customiseControlPoints(
  obj: FabricObject,
  isLocked: boolean = false
) {
  if (!isLocked) {
    obj.setControlsVisibility({
      tr: true,
      tl: true,
      bl: true,
      br: true,
      mt: false,
      mb: false,
      mtr: true,
      ml: true,
      mr: true,
    });
  }
}

export function customiseLineControlPoints(
  obj: FabricObject,
  isLocked: boolean = false
) {
  if (isLocked) {
    return;
  }

  obj.setControlsVisibility({
    tr: false,
    tl: false,
    bl: false,
    br: false,
    mt: false,
    mb: false,
    mtr: true,
    ml: false,
    mr: false,
  });

  if (!(obj instanceof Polyline)) {
    return;
  }

  const line = obj as Polyline;

  const deleteControl = line.controls.deleteControl;
  const rotateControl = line.controls.mtr;
  line.controls = controlsUtils.createPolyControls(line);
  // add back the delete and rotate controls
  if (deleteControl) {
    line.controls.deleteControl = deleteControl;
  }
  if (rotateControl) {
    line.controls.mtr = rotateControl;
  }
}

export function initialiseShapeCursor(
  obj: FabricObject,
  isLocked: boolean = false
) {
  // Don't set custom cursors on mobile devices
  if (isMobileBrowser()) {
    return;
  }

  if (isLocked) {
    obj.set({
      hoverCursor: 'default',
    });
  } else {
    obj.set({
      moveCursor: 'none',
      hoverCursor: 'none',
    });
  }
}

function loadDeleteIcon(): Promise<FabricImage> {
  return new Promise(resolve => {
    FabricImage.fromURL(trash, { crossOrigin: 'anonymous' }).then(img => {
      img.scaleToWidth(16);
      img.scaleToHeight(16);
      img.originX = 'left';
      img.originY = 'top';
      resolve(img);
    });
  });
}

export async function addDeleteControl(canvas: Canvas) {
  const deleteIcon = await loadDeleteIcon();

  return new Control({
    x: 0.5, // position (right side)
    y: -0.55, // position (top)
    offsetX: 0,
    offsetY: -20,
    cursorStyle: 'pointer',
    sizeX: 32,
    sizeY: 32,

    mouseUpHandler: function (eventData, transform) {
      const target = transform.target;
      canvas.remove(target);
      canvas.requestRenderAll();
      return true;
    },
    render: function (ctx, left, top) {
      if (deleteIcon) {
        const size = 32;

        ctx.save();
        ctx.translate(left, top);

        ctx.beginPath();
        ctx.arc(0, 0, size / 2, 0, 2 * Math.PI, false);
        ctx.fillStyle = grey1;
        ctx.fill();

        ctx.lineWidth = 1;
        ctx.strokeStyle = grey4;
        ctx.stroke();

        deleteIcon.set({
          left: -8,
          top: -8,
        });
        deleteIcon.render(ctx);
        ctx.restore();
      }
    },
  });
}

export async function initialiseDeleteControl(
  canvas: Canvas,
  object: Textbox | Path | Circle | Triangle | Polyline,
  isLocked: boolean = false
) {
  if (!isLocked) {
    object.controls.deleteControl = await addDeleteControl(canvas);
  }
}

export function addTextListeners(canvas: Canvas, text: Textbox) {
  text.on('editing:entered', function () {
    const currentText = text.text;
    if (currentText === TEXT_PLACEHOLDER) {
      text.set({ text: '', fill: '#000' });
      if (text.hiddenTextarea) {
        text.hiddenTextarea.value = '';
      }
      canvas.requestRenderAll();
    }
  });

  text.on('editing:exited', function () {
    const currentText = text.text;
    if (currentText.trim() === '') {
      // reset placeholder style
      text.set({ text: TEXT_PLACEHOLDER, fill: '#aaa' });
      if (text.hiddenTextarea) {
        text.hiddenTextarea.value = TEXT_PLACEHOLDER;
      }
      canvas.requestRenderAll();
    }
  });
}
